import {
  Avatar,
  Box,
  Card,
  Grid,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AuthApi from '../../../apis/AuthApi';
import useApi from '../../../lib/useApi';
import Section from '../components/Section';
import TodoItem from '../components/TodoItem';

/**
 * Information for the users course progress
 */
export interface CourseProgress {
  /**
   * The title of the course
   */
  title: string;
  /**
   * The progress the user is making through the course between 0-100%
   */
  progress: number;
  /**
   * Whether the organization requires the training
   */
  required: boolean;

  /**
   * The slug for the course's navigation
   */
  slug: string;
}

/**
 * The properties for the personal section component
 */
interface PersonalSectionProps {
  /**
   * The course progress for the user
   */
  courseProgress: CourseProgress[];
}

/**
 * A section that's personalized for the user
 */
const PersonalSection = (props: PersonalSectionProps) => {
  const theme = useTheme();
  const requiredTraining = useMemo(
    () => props.courseProgress.filter((c) => c.required),
    [props.courseProgress]
  );
  const optionalTraining = useMemo(
    () => props.courseProgress.filter((c) => !c.required),
    [props.courseProgress]
  );
  const authApi = useApi(AuthApi);
  const [firstName, setFirstName] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const userFirstName = await authApi.GetUserFirstName();
        setFirstName(userFirstName);
      } catch (error) {
        console.error('Failed to fetch user name:', error);
      }
    };

    fetchUserName();
  }, [authApi]);

  const welcome = useMemo(() => {
    const currentHours = new Date().getHours();
    const welcomeInfo = {
      message: '',
      image: '',
    };

    // Set the image
    if (currentHours >= 5 && currentHours < 11) {
      welcomeInfo.image = 'welcome-morning.jpg';
    } else if (currentHours >= 11 && currentHours < 17) {
      welcomeInfo.image = 'welcome-day.jpg';
    } else if (currentHours >= 17 && currentHours <= 20) {
      welcomeInfo.image = 'welcome-evening.jpg';
    } else {
      welcomeInfo.image = 'welcome-night.jpg';
    }

    // Set the message
    if (currentHours >= 4 && currentHours < 12) {
      welcomeInfo.message = 'Good morning';
    } else if (currentHours >= 12 && currentHours < 18) {
      welcomeInfo.message = 'Good afternoon';
    } else {
      welcomeInfo.message = 'Good evening';
    }

    return welcomeInfo;
  }, []);

  return (
    <Section title="For You">
      <Grid container spacing="1em">
        <Grid
          item
          xs={12}
          sm={5}
          md={12}
          sx={{ height: { xs: 'auto', md: '6em' } }}
        >
          <Box
            sx={{
              padding: '1em',
              fontWeight: '600',
              borderRadius: '.4em',
              backgroundImage: `url(${welcome.image})`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              color: 'white',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h3"
              fontSize="1.5em"
              fontWeight="600"
              sx={{
                textShadow: '2px 2px 6px rgba(0, 0, 0, 0.8)', // Subtle shadow for readability
              }}
            >
              {welcome.message} {firstName && `, ${firstName}`}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={7} md={12}>
          <Box
            sx={{
              backgroundColor: '#1976d2',
              padding: '1em',
              color: 'white',
              borderRadius: '.4em',
            }}
          >
            {requiredTraining.length > 0 && (
              <>
                <Typography
                  fontWeight="600"
                  color="white"
                  fontSize="1.1em"
                  marginBottom=".4em"
                >
                  Required Training
                </Typography>
                {requiredTraining.map((t, i) => (
                  <TodoItem
                    key={i}
                    title={t.title}
                    complete={t.progress == 100}
                    slug={t.slug}
                  />
                ))}
              </>
            )}
            {optionalTraining.length > 0 && (
              <>
                <Typography
                  fontWeight="600"
                  color="white"
                  marginBottom=".25em"
                  fontSize="1.1em"
                  marginTop={requiredTraining.length > 0 ? '1em' : 0}
                >
                  Optional Training
                </Typography>
                {optionalTraining.map((t, i) => (
                  <TodoItem
                    key={i}
                    title={t.title}
                    complete={t.progress == 100}
                    slug={t.slug}
                  />
                ))}
              </>
            )}
            <Box marginTop=".5em">
              <Typography variant="caption">
                We will check this list off automatically as you complete the
                training.
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={9} md={12} sx={{ width: '100%' }}>
          <Card elevation={3} sx={{ padding: '1em' }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-evenly"
              width="100%"
              marginY="1em"
            >
              <Avatar
                src="jim-profile.png"
                sx={{ height: '5em', width: '5em', marginRight: '.5em' }}
              />
              <Box display="flex" flexDirection="column" alignItems="center">
                <Typography
                  fontWeight="700"
                  fontSize="1.5em"
                  marginBottom="-0.3em"
                >
                  Jim Warwick
                </Typography>
                <Typography color={theme.palette.text.secondary}>
                  Founder of S3G Security
                </Typography>
              </Box>
            </Box>
            <Typography variant="body1" gutterBottom>
              &emsp;&emsp;Thank you for taking the time to be here. We&apos;re
              truly excited for you to start this training. Every skill you gain
              contributes to a safer and more prepared environment for everyone
              around you. Know that you&apos;re not alone in this—our team is
              here to support you every step of the way. We&apos;re grateful for
              your commitment to making a difference, and we&apos;re honored to
              be part of your journey.
            </Typography>
            <Link to="/about" component={RouterLink} color="textPrimary">
              Learn more
            </Link>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3} md={12} sx={{ width: '100%' }}>
          <Card elevation={3} sx={{ padding: '1em', height: '100%' }}>
            <Grid container spacing={1} sx={{ height: '100%' }}>
              <Grid
                item
                xs={4}
                sm={12}
                lg={4}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography fontWeight="700" fontSize="1.5em">
                  Follow us
                </Typography>
              </Grid>
              <Grid
                item
                xs={8}
                sm={12}
                lg={8}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'row', sm: 'column', md: 'row' },
                  justifyContent: {
                    xs: 'space-around',
                    sm: 'space-between',
                    md: 'space-around',
                  },
                  alignItems: 'center',
                }}
              >
                <Box
                  component={Link}
                  href="https://www.linkedin.com/in/jim-warwick-ed-d-399871112"
                  target="_blank"
                  sx={{
                    backgroundColor: 'white',
                    backgroundImage: 'url(/linkedin.png)',
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    borderRadius: '10px',
                    boxShadow: (t) =>
                      t.palette.mode == 'light'
                        ? '0px 0px 9px 0px #ababab'
                        : 'initial',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.02)',
                    },
                  }}
                  height="50px"
                  width="150px"
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Section>
  );
};

export default PersonalSection;
